import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  DownOutlined,
  ProjectOutlined,
  ShoppingOutlined,
  UserAddOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Divider,
  Dropdown,
  MenuProps,
  Modal,
  Result,
  Select,
  Space,
  Spin,
  Table,
  TableColumnType,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import ModalCreatePurchaseOrder from "../../components/modal/ModalCreatePurchaseOrder";
import ModalViewPurchaseOrder from "../../components/modal/ModalViewPurchaseOrder";
import { PurchaseRequestStatus } from "../../enum/common";
import { DepartmentCode, UserRole } from "../../enum/roles";
import { IPurchaseRequestItem } from "../../models/Purchase";
import { useGetUserByDepartmentCode } from "../../query/DepartmentQuery";
import {
  useApprovePurchaseRequestByPurchase,
  useAssignPurchaseOrder,
  useGetPurchaseRequestByAlias,
} from "../../query/PurchaseQuery";
import { RootState } from "../../store";
import { notify } from "../../utils/toast";

const { Text } = Typography;

const PurchaseRequestDetail = () => {
  const { alias } = useParams<{ alias: string }>();
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);
  const { data: order, isLoading } = useGetPurchaseRequestByAlias(alias);
  const approveMutation = useApprovePurchaseRequestByPurchase();
  const { data: usersInPurchase, isLoading: isLoadingUsersInPurchase } =
    useGetUserByDepartmentCode(DepartmentCode.PUR);
  const assignPurchaseOrderMutation = useAssignPurchaseOrder();
  const [assignModalVisible, setAssignModalVisible] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openModalViewOrder, setOpenModalViewOrder] = useState<boolean>(false);
  const handleApprove = async () => {
    if (!order) return;
    try {
      await approveMutation.mutateAsync(order.id);
      notify.success("Đã duyệt đơn hàng thành công");
    } catch (error) {
      notify.error("Đã có lỗi xảy ra");
    }
  };
  const handleAssignUser = async (userId: string) => {
    if (!order) return;
    try {
      await assignPurchaseOrderMutation.mutateAsync({
        id: order.id,
        assigneeId: userId,
      });
      notify.success("Đã gán người phụ trách thành công");
    } catch (error) {
      notify.error("Gán người phụ trách thất bại");
    }
  };

  const menuItems: MenuProps["items"] = [
    {
      key: "approve",
      label: "Duyệt",
      icon: <CheckCircleOutlined />,
      disabled: order?.status !== PurchaseRequestStatus.SUBMITTED,
      onClick: handleApprove,
    },
    {
      key: "assign",
      label: "Gán người phụ trách",
      icon: <UserAddOutlined />,
      disabled:
        (order?.status === PurchaseRequestStatus.DRAFT ||
          userInfo?.position.level !== "MANAGER") &&
        userInfo?.role !== UserRole.SUPER_ADMIN,
      onClick: () => setAssignModalVisible(true),
    },
    {
      key: "reject",
      label: "Từ chối",
      icon: <CloseCircleOutlined />,
      danger: true,
      disabled: order?.status !== PurchaseRequestStatus.SUBMITTED,
    },
    {
      key: "create-orders",
      label: "Tạo đơn đặt hàng",
      icon: <ShoppingOutlined />,
      disabled: order?.status !== PurchaseRequestStatus.APPROVED,
      onClick: () => setOpenModal(true),
    },
    {
      key: "view-orders",
      label: "Xem đơn hàng",
      icon: <ProjectOutlined />,
      disabled:
        order?.status !== PurchaseRequestStatus.APPROVED &&
        order?.status !== PurchaseRequestStatus.CLOSED,
      onClick: () => setOpenModalViewOrder(true),
    },
  ];

  if (isLoading || isLoadingUsersInPurchase) {
    return (
      <div className="p-4 flex justify-center items-center h-screen">
        <Spin size="large" />
      </div>
    );
  }
  const columns: TableColumnType<IPurchaseRequestItem>[] = [
    {
      title: "STT",
      key: "index",
      width: 60,
      render: (_: any, __: any, i: number) => i + 1,
    },
    { title: "Mã nội bộ", dataIndex: "internalCode", key: "internalCode" },
    { title: "Part Number", dataIndex: "partNumber", key: "partNumber" },
    { title: "Tên khác", dataIndex: "description", key: "description" },
    {
      title: "Danh mục",
      dataIndex: "category",
      key: "category",
    },
    {
      title: "Số lượng",
      dataIndex: "quantity",
      key: "quantity",
    },
    {
      title: "Đơn vị tính",
      dataIndex: "unit",
      key: "unit",
    },
    {
      title: "Nhà sản xuất",
      dataIndex: "manufacturer",
      key: "manufacturer",
    },
    {
      title: "CRD (Customer Request Date)",
      dataIndex: "crd",
      key: "crd",
      render: (values: Date) => (
        <Text>{dayjs(values).format("DD/MM/YYYY")}</Text>
      ),
    },
    {
      title: "Nguồn mua",
      dataIndex: "source",
      key: "source",
    },
    {
      title: "Đơn giá EXW (USD)",
      dataIndex: "unitPriceExwUsd",
      key: "unitPriceExwUsd",
      render: (values: number) => <Text>{values}</Text>,
    },
    {
      title: "Tổng tiền EXW (USD)",
      dataIndex: "totalPriceExwUsd",
      key: "totalPriceExwUsd",
      render: (values: number) => <Text>{values}</Text>,
    },
    {
      title: "Đơn giá (VND)",
      dataIndex: "unitPrice",
      key: "unitPrice",
      render: (price: number) => `${Number(price).toLocaleString()}`,
    },
    {
      title: "Tổng tiền (VND)",
      dataIndex: "totalPriceVnd",
      key: "totalPriceVnd",
      render: (price: number) => `${Number(price).toLocaleString()}`,
    },

    {
      title: "Phí ship (VND)",
      dataIndex: "shippingCost",
      key: "shippingCost",
      render: (fee: number) => `${Number(fee).toLocaleString()}`,
    },
    {
      title: "Thuế NK (%)",
      dataIndex: "importTax",
      key: "importTax",
      render: (percent: number) => <Text>{Number(percent)}</Text>,
    },
    {
      title: "Tổng thuế (VND)",
      dataIndex: "totalImportTaxVnd",
      key: "totalImportTaxVnd",
      render: (price: number) => `${Number(price).toLocaleString()}`,
    },
    {
      title: "Thành tiền (VND)",
      dataIndex: "grandTotalVnd",
      key: "grandTotalVnd",
      render: (price: number) => `${Number(price).toLocaleString()}`,
    },
    {
      title: "Link",
      dataIndex: "link",
      key: "link",
      render: (link: string) => {
        if (!link) return "-";
        return (
          <a href={link} target="_blank" rel="noopener noreferrer">
            {link}
          </a>
        );
      },
    },
  ];

  const getStatusTag = (status: PurchaseRequestStatus) => {
    switch (status) {
      case PurchaseRequestStatus.DRAFT:
        return (
          <Tag icon={<ClockCircleOutlined />} color="orange">
            Nháp
          </Tag>
        );
      case PurchaseRequestStatus.SUBMITTED:
        return (
          <Tag icon={<UserAddOutlined />} color="purple">
            Đã gửi yêu cầu
          </Tag>
        );
      case PurchaseRequestStatus.APPROVED:
        return (
          <Tag icon={<CheckCircleOutlined />} color="blue">
            Đã duyệt
          </Tag>
        );
      case PurchaseRequestStatus.REJECTED:
        return (
          <Tag icon={<CloseCircleOutlined />} color="red">
            Từ chối
          </Tag>
        );
      case PurchaseRequestStatus.CLOSED:
        return (
          <Tag icon={<CheckCircleOutlined />} color="green">
            Hoàn thành/Đã đóng
          </Tag>
        );
      default:
        return <Tag>{status}</Tag>;
    }
  };
  if (!order)
    return (
      <Result status="404" title="404" subTitle="Không tìm thấy đơn hàng" />
    );
  return (
    <div className="p-4">
      <Modal
        title="Gán người phụ trách"
        open={assignModalVisible}
        onCancel={() => setAssignModalVisible(false)}
        footer={null}
      >
        <div className="mt-4">
          <Select
            placeholder="Chọn người phụ trách"
            style={{ width: "100%" }}
            value={order?.assigneeId || undefined}
            onChange={(value) => {
              handleAssignUser(value);
              setAssignModalVisible(false);
            }}
            loading={isLoadingUsersInPurchase}
            options={usersInPurchase?.map((user) => ({
              label: user.fullName,
              value: user.id,
            }))}
            size="large"
          />
        </div>
      </Modal>

      <div className="mb-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
          <div>
            <Typography.Title
              level={3}
              style={{
                margin: 0,
                fontFamily: "Chakra Petch",
                textTransform: "uppercase",
              }}
              className="dark:text-white"
            >
              {order.requestNumber}
            </Typography.Title>
            <Space wrap className="mt-2">
              <Tag icon={<UserOutlined />} color="green">
                Người yêu cầu: {order.requestBy}
              </Tag>
              <Tag icon={<ProjectOutlined />} color="purple">
                Dự án: {order.projectName}
              </Tag>
              <Tag icon={<ShoppingOutlined />} color="cyan">
                Yêu cầu từ: {order.source}
              </Tag>
              <Tag
                color={
                  order.priority === "HIGH"
                    ? "red"
                    : order.priority === "MEDIUM"
                      ? "yellow"
                      : "green"
                }
              >
                Mức độ:{" "}
                {order.priority === "HIGH"
                  ? "Rất gấp"
                  : order.priority === "MEDIUM"
                    ? "Gấp"
                    : "Thường"}
              </Tag>
              <Tag icon={<DollarOutlined />} color="volcano">
                Tỉ giá: {order.exchangeRate}
              </Tag>

              <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
                <Button icon={<DownOutlined />}>Chức năng</Button>
              </Dropdown>
            </Space>
          </div>
          <div className="text-right">
            {getStatusTag(order.status)}
            <div className="mt-2">
              <Text type="secondary">Ngày tạo: </Text>
              <Text strong style={{ color: "blue" }}>
                {dayjs(order.createdAt).format("DD/MM/YYYY")}
              </Text>
            </div>
            <div className="mt-2">
              <Text type="secondary">Hạn: </Text>
              <Text strong style={{ color: "#d4380d" }}>
                {dayjs(order.deadline).format("DD/MM/YYYY")}
              </Text>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Card size="small" className="bg-white dark:bg-gray-800">
          <div className="flex items-center gap-2 mb-2">
            <UserOutlined className="text-blue-500" />
            <Text strong>Người yêu cầu</Text>
          </div>
          <Text className="text-lg block">{order.requestBy}</Text>
          <Text type="secondary" className="text-sm">
            {dayjs(order.createdAt).format("DD/MM/YYYY")}
          </Text>
        </Card>

        <Card size="small" className="bg-white dark:bg-gray-800">
          <div className="flex items-center gap-2 mb-2">
            <UserAddOutlined className="text-cyan-500" />
            <Text strong>Người phụ trách</Text>
          </div>
          {order.assigneeId ? (
            <div>
              <Text className="text-lg block">
                {
                  usersInPurchase?.find((user) => user.id === order.assigneeId)
                    ?.fullName
                }
              </Text>
            </div>
          ) : (
            <div>
              <Text className="text-lg block">Chưa có người phụ trách</Text>
            </div>
          )}
        </Card>

        <Card size="small" className="bg-white dark:bg-gray-800">
          <div className="flex items-center gap-2 mb-2">
            <DollarOutlined className="text-orange-500" />
            <Text strong>Tổng tiền ước tính</Text>
          </div>
          <Text strong className="text-xl block" style={{ color: "#fa8c16" }}>
            {Number(order.estimatedAmount).toLocaleString()} VNĐ
          </Text>
        </Card>

        <Card size="small" className="bg-white dark:bg-gray-800">
          <div className="flex items-center gap-2 mb-2">
            <ShoppingOutlined className="text-purple-500" />
            <Text strong>Số món hàng</Text>
          </div>
          <Text className="text-xl block">{order.items.length}</Text>
        </Card>
      </div>

      <Card
        title="Danh sách vật tư"
        size="small"
        className="mb-4"
        extra={<Tag color="blue">{order.items.length} món</Tag>}
      >
        <Table
          dataSource={order.items}
          columns={columns}
          rowKey="id"
          pagination={false}
          scroll={{ x: 1000 }}
          size="small"
        />
      </Card>
      {order.note && (
        <Card title="Ghi chú" size="small" className="mb-4">
          <Text>{order.note}</Text>
        </Card>
      )}

      <Divider />
      {openModal && (
        <ModalCreatePurchaseOrder
          open={openModal}
          purchaseRequest={order}
          onClose={() => setOpenModal(false)}
        />
      )}
      {openModalViewOrder && (
        <ModalViewPurchaseOrder
          open={openModalViewOrder}
          purchaseRequestId={order.id}
          onClose={() => setOpenModalViewOrder(false)}
        />
      )}
    </div>
  );
};

export default PurchaseRequestDetail;
